import React, { useCallback, useMemo, useState } from 'react';
import {
  ClientTokenContext,
  parseToken,
  TokenPayload,
  useAuth,
  useLogger,
} from '@cko/dashboard-shared';
import { AUTH_ERRORS } from '../errors';

type EntitySegments = Record<string, string[] | string>;

type Props = {
  children: React.ReactNode;
  initialClientID?: string;
  initialToken?: string;
};

export const ClientTokenManagerProvider = ({ initialClientID, initialToken, children }: Props) => {
  const { authService } = useAuth();
  const [clientID, setClientID] = useState<string | undefined>(initialClientID);
  const [accessToken, setAccessToken] = useState<string | undefined>(initialToken);
  const [loading, setLoading] = useState<boolean>(false);
  const logger = useLogger();

  const refreshToken = useCallback(
    async (newClientID?: string): Promise<void> => {
      if (newClientID === clientID) {
        return;
      }

      const clientToRefresh = newClientID || clientID;

      if (!clientToRefresh) {
        logger.warn('A client has not been set. Call the function with a clientID');
        return;
      }

      try {
        setLoading(true);
        const newAccessToken = await authService.getTokenForMerchant(clientToRefresh);
        setAccessToken(newAccessToken);
        setClientID(clientToRefresh);
        setLoading(false);
      } catch (error) {
        logger.error(AUTH_ERRORS.clientTokenManager, error);
        setLoading(false);
        throw error;
      }
    },
    [authService, clientID],
  );

  const {
    cko_entity_segments: entitySegments = {},
    cko_permissions: permissions = [],
    cko_client_id: clientId = undefined,
    cko_sub_entity_id: subEntityId = undefined,
    cko_user_id: userId = '',
    cko_user_roles: userRoles = [],
    cko_user_type: userType = 'External',
    sub = '',
  }: TokenPayload = parseToken(accessToken || '');

  let isSegmentsOnlyUser = true;

  if (Object.keys(entitySegments).length === 0) {
    isSegmentsOnlyUser = false;
  } else {
    // check if any entity has a "*" in which case the user has full access to that entity.
    Object.values(entitySegments).forEach((segments: string[] | string) => {
      if (!Array.isArray(segments) && segments === '*') {
        isSegmentsOnlyUser = false;
      }
    });
  }

  // While the entities array does not exist in the token, this enables backwards compatibility.
  const getEntityIdsFromEntitySegments = (entitySegmentsFromToken: EntitySegments): string[] => {
    if (!entitySegmentsFromToken) {
      return [];
    }

    const entityIds = Object.keys(entitySegmentsFromToken);
    if (entityIds.length === 0) {
      return [];
    }

    return entityIds;
  };
  const entities = getEntityIdsFromEntitySegments(entitySegments);

  const contextValues = useMemo(
    () => ({
      accessToken,
      tokenProperties: {
        clientId,
        entities,
        entitySegments,
        permissions,
        sub,
        subEntityId,
        userId,
        userRoles,
        userType,
      },
      loading,
      isSegmentsOnlyUser,
      actions: {
        refreshToken,
      },
    }),
    [
      accessToken,
      clientId,
      entities,
      entitySegments,
      isSegmentsOnlyUser,
      loading,
      permissions,
      refreshToken,
      sub,
      subEntityId,
      userId,
      userRoles,
      userType,
    ],
  );

  return (
    <ClientTokenContext.Provider value={contextValues}>{children}</ClientTokenContext.Provider>
  );
};
